import { React, useNavigate, useState, useEffect } from '../../utils/globalImports';
import { AppLayout, FilterCard, CustomDataTable, useApi, Toaster, validationMessages, CustomBackdrop } from '../../utils/globalImports';
import { ApiResponse } from '../../services/apiService';

interface Client {
    clientId: number;
    clientName: string;
    clientNumber: string;
    status: number;
    categoryId: string;
    regionId: string;
    categoryName: string;
    regionName: string;
}

const Clients: React.FC = () => {

    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [clientDatalist, setClientDatalist] = useState<any[]>([]);
    const [selectedClient, setSelectedClient] = useState<any | null>(null);
    const [clientData, setClientData] = useState<any[]>([]);
    const [currentStatus, setcurrentStatus] = useState<string[]>(['Active']);
    const [filteredData, setFilteredData] = useState<any[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false); // State to manage confirm dialog "open/close" state
    const [isOn, setIsOn] = useState<boolean>();  // State to manage the toggle button's "on/off" state
    const { execute, loading } = useApi();
    const props = { showClientDropdown: true, title: 'Clients', actionText: 'Add Client', searchbarText: validationMessages.client_searchText }
    const userId = Number(localStorage.getItem("userId"));

    useEffect(() => {
        fetchClients();
    }, []);  // The empty dependency array ensures it only runs once when the component mounts

    const navigate = useNavigate();
    const handleNavigate = (clientId?: number) => {
        if (clientId) navigate(`/client/add-client/${clientId}`);
        else navigate('/client/add-client');
    }

    const sortOptions = [
        { value: "Client Name", text: "Name" },
        { value: "Client Number", text: "Client Number" },
    ];

    // Fetch clients
    const fetchClients = async () => {
        const { data } = await execute<Client[]>('get', '/Client/GetAllClients');
        if (data) {
            setClientDatalist(data)
            // Use reduce to exclude unwanted fields
            const clientData = data
                .map((client) => {
                    const { clientId, clientNumber, clientName, categoryId, regionId, status, categoryName, regionName } = client;
                    const Status = status === 1 ? 'Active' : 'Inactive';
                    return { clientId, categoryId, regionId, "Client Number": clientNumber, "Client Name": clientName, category: categoryName, region: regionName, Status };
                });
            setClientData(clientData);
            // Filter for status = 1 (Active) and set to filteredData
            const activeClients = clientData
                .filter((client) => currentStatus.includes(client.Status))
                .sort((a, b) =>
                    a["Client Name"].localeCompare(b["Client Name"], undefined, { sensitivity: 'base' }) ||
                    a["Client Number"].localeCompare(b["Client Number"], undefined, { sensitivity: 'base' })
                );
            setFilteredData(activeClients);
        }
    };

    // filter Data
    const handleSearchClientData = (query: string, status: Number, category?: number, region?: number) => {
        const lowerCaseQuery = query.toLowerCase();

        // Map status strings to corresponding numbers
        const statusMapping: Record<string, number> = {
            Active: 1,
            Inactive: 0,
        };

        const filtered = clientData.filter((item) => {

            // Convert item.Status to its numeric equivalent or skip if it doesn't match
            const itemStatus = statusMapping[item.Status] ?? -1;

            const matchesQuery =
                item["Client Name"].toLowerCase().includes(lowerCaseQuery) ||
                item["Client Number"].toLowerCase().includes(lowerCaseQuery);

            const matchCategory =
                category === -1 || item.categoryId === category;

            const matchRegion =
                region === -1 || item.regionId === region;

            const matchesStatus =
                status === -1 || itemStatus === status;

            return matchesQuery && matchCategory && matchRegion && matchesStatus;
        })
            .sort((a, b) =>
                a["Client Name"].localeCompare(b.clientName, undefined, { sensitivity: 'base' }) ||
                a["Client Number"].localeCompare(b.clientNumber, undefined, { sensitivity: 'base' })
            );
        setFilteredData(filtered);
        setcurrentStatus(
            status === 0
                ? ['Inactive'] // When status is 0, set currentStatus to 'Inactive'
                : status === -1
                    ? ['Active', 'Inactive'] // When status is -1 or 2, set currentStatus to both 'Active' and 'Inactive'
                    : status === 1
                        ? ['Active'] // When status is 1, set currentStatus to 'Active'
                        : currentStatus // Fallback to the current value if no conditions are met
        );
    }

    const handleToggleState = (row: any) => {
        if (row.Status === 'Inactive') {
            setIsOn(false)
        } else {
            setIsOn(true)
        }
        setSelectedClient(row);
        setDialogOpen(true);
    }

    const handleEdit = (client: any) => {
        handleNavigate(client.clientId)
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
        setSelectedClient(null); // Clear selected row
    }

    const handleDialogConfirm = async () => {
        if (!selectedClient) return;
        try {
            // Make API call to update the status
            const { data } = await execute<Client>('get', `/Client/GetClient/${selectedClient.clientId}`);
            if (data) {
                // Step 2: Update the fields on the filtered record
                const updatedClientData = {
                    ...data, // Keep the original user data
                    status: selectedClient.Status === 'Active' ? 0 : 1,
                    updatedId: userId, // You can also update any additional fields like updatedId
                };

                let response: ApiResponse<any> = await execute('put', '/Client/UpdateClient', updatedClientData);

                if (response!) {
                    if (response.error) {
                        // If there's an error in the response, display the error message
                        setToast({ open: true, message: response.error, type: "error" });
                    } else {
                        // If the response is successful, display the success message
                        setToast({ open: true, message: response.data, type: "success" });
                    }
                }
            }
            fetchClients();
            setSelectedClient(null); // Clear selected row
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setDialogOpen(false);
        }
    };

    return (
        <AppLayout>
            <FilterCard {...props} handleModalOrNavigate={() => handleNavigate()} handleSearch={handleSearchClientData} ></FilterCard>
            {clientDatalist.length > 0 && (<CustomDataTable rows={filteredData} actionText="Client" sortOptions={sortOptions} onRowEdit={handleEdit} onRowDoubleClick={handleEdit} onToggleClick={handleToggleState} isDialogOpen={dialogOpen} isToggleOn={isOn} handleCancel={handleDialogCancel} handleConfirm={handleDialogConfirm} noResultMessage={validationMessages.common_RecordNotFound("clients")}></CustomDataTable>)}
            <CustomBackdrop open={loading} />
            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </AppLayout>
    );
};

export default Clients;

